import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CollectionStyleItemVariant, TranslationContext } from "@is/constants";
import { CollectionStyle } from "@is/types";
import { convertCurrencyCodeToSymbol } from "@is/utils";

type Props = {
  collectionStyle: CollectionStyle;
  variant?: CollectionStyleItemVariant;
  index: number;
  onAdd?: (styleId: string, variantId: string) => void;
  onRemove?: (index: number) => void;
};

const useManageCollectionStyleItem = ({
  collectionStyle,
  variant,
  index,
  onAdd,
  onRemove,
}: Props) => {
  const { t } = useTranslation(TranslationContext.Settings);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: collectionStyle.variantId, disabled: false });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isPending, setIsPending] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleRemoveStyleClick = useCallback(async () => {
    if (variant === CollectionStyleItemVariant.IN_COLLECTION && onRemove) {
      setIsPending(true);
      await onRemove(index);
      setIsPending(false);
    }
  }, [variant, onRemove, index]);

  const handleAddStyleClick = useCallback(async () => {
    if (onAdd) {
      setIsPending(true);
      await onAdd(collectionStyle.styleId, collectionStyle.variantId);
      setIsDisabled(true);
      setIsPending(false);
    }
  }, [onAdd, collectionStyle]);

  const isTransparent = useMemo(
    () =>
      (variant === CollectionStyleItemVariant.IN_COLLECTION ||
        variant === CollectionStyleItemVariant.IN_ALL_ITEMS) &&
      ((!collectionStyle.hasInstoreStock && !collectionStyle.hasOnlineStock) ||
        !collectionStyle.price),
    [variant, collectionStyle]
  );

  const addButtonState: "disabled" | "pending" | undefined = useMemo(() => {
    if (collectionStyle.isAddedToCollection || isDisabled) {
      return "disabled";
    } else if (isPending) {
      return "pending";
    }

    return undefined;
  }, [collectionStyle, isPending, isDisabled]);

  useEffect(() => {
    if (collectionStyle) {
      setIsDisabled(collectionStyle.isAddedToCollection);
    }
  }, [collectionStyle]);

  const collectionItem: CollectionStyle & { isNotFullData?: boolean } =
    useMemo(() => {
      const defaultCollectionStyle: CollectionStyle = {
        color: { name: "", mainColorName: "" },
        hasInstoreStock: false,
        hasOnlineStock: false,
        mediaUrl: "",
        price: { currencyCode: "", retailPrice: 0 },
        styleId: "",
        styleName: "",
        variantId: "",
        variantName: "",
        ...(variant === CollectionStyleItemVariant.IN_SEARCH && {
          isAddedToCollection: false,
        }),
      };

      const isNotFullData =
        Object.keys(collectionStyle).length <
        Object.keys(defaultCollectionStyle).length;

      return {
        ...defaultCollectionStyle,
        ...collectionStyle,
        ...(isNotFullData && { isNotFullData: true }),
      };
    }, [collectionStyle, variant]);

  const price = useMemo(
    () => ({
      retailPrice: collectionItem.price.retailPrice
        ? `${collectionItem.price.retailPrice}`
        : "",
      currency: collectionItem.price.currencyCode
        ? convertCurrencyCodeToSymbol(collectionItem.price.currencyCode)
        : "",
      salePrice: collectionItem.price.salePrice
        ? `${collectionItem.price.salePrice}`
        : "",
    }),
    [collectionItem]
  );

  return {
    t,
    isPending,
    isTransparent,
    addButtonState,
    handleRemoveStyleClick,
    handleAddStyleClick,
    style,
    setNodeRef,
    attributes,
    listeners,
    collectionItem,
    price,
  };
};

export default useManageCollectionStyleItem;
